import React from "react";

import { ConfirmModal } from "../../components/modals/confirm-modal";
import { GalleryLabel } from "@/models/gallery-label.model";
import {
  createGalleryLabelRequest,
  deleteGalleryLabelRequest,
  getGalleryLabelsRequest,
  updateGalleryLabelRequest,
} from "./store/admin.api";
import styles from "./admin-theme.module.css";

const CREATE_NAME_ID = "gallery-labels-create-name";

/** Shared width for the “New label” card and the labels table */
const LABELS_PANEL_STYLE: React.CSSProperties = { maxWidth: "42rem", width: "100%" };

const toGalleryLabelItems = (response: { data?: any }): GalleryLabel[] => {
  const body = response?.data;
  if (!body) return [];
  const candidates = [body.labels, body.data?.items, body.data?.labels, body.items, body.data];
  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as GalleryLabel[];
    }
  }
  return [];
};

export const GalleryLabelsPage = () => {
  const [labels, setLabels] = React.useState<GalleryLabel[]>([]);
  const [nameDrafts, setNameDrafts] = React.useState<Record<string, string>>({});
  const [newName, setNewName] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [savingId, setSavingId] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [labelPendingDelete, setLabelPendingDelete] = React.useState<GalleryLabel | null>(null);

  const loadLabels = React.useCallback(async () => {
    setLoading(true);
    const response = await getGalleryLabelsRequest();
    setLoading(false);

    if (response.data?.status === false) {
      setError(response.data?.msg || "Failed to load gallery labels");
      setLabels([]);
      return;
    }

    setError(null);
    setLabels(toGalleryLabelItems(response));
  }, []);

  React.useEffect(() => {
    loadLabels();
  }, [loadLabels]);

  React.useEffect(() => {
    setNameDrafts((current) => {
      const next: Record<string, string> = {};
      labels.forEach((label) => {
        const existing = current[label._id];
        next[label._id] = existing !== undefined && existing !== label.name ? existing : label.name;
      });
      return next;
    });
  }, [labels]);

  const handleCreate = async () => {
    const trimmed = newName.trim();
    if (!trimmed) return;

    setSavingId("new");
    setError(null);
    const response = await createGalleryLabelRequest({ name: trimmed });
    setSavingId(null);

    if (!response.data?.status) {
      setError(response.data?.msg || response.data?.error?.message || "Failed to create label");
      return;
    }

    setNewName("");
    await loadLabels();
  };

  const handleSave = async (label: GalleryLabel) => {
    const draft = (nameDrafts[label._id] ?? label.name).trim();
    if (!draft) {
      setError("Label name cannot be empty");
      return;
    }

    setSavingId(label._id);
    setError(null);
    const response = await updateGalleryLabelRequest(label._id, { name: draft });
    setSavingId(null);

    if (!response.data?.status) {
      setError(response.data?.msg || response.data?.error?.message || "Failed to update label");
      return;
    }

    await loadLabels();
  };

  const executeDeleteLabel = async (label: GalleryLabel): Promise<boolean> => {
    setSavingId(label._id);
    setError(null);
    const response = await deleteGalleryLabelRequest(label._id);
    setSavingId(null);

    if (!response.data?.status) {
      setError(response.data?.msg || response.data?.error?.message || "Failed to delete label");
      return false;
    }

    await loadLabels();
    return true;
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3 className="mb-1">Gallery labels</h3>
          <div className="text-white-50 small">
            Create and manage labels that can be assigned to galleries. Names must be unique.
          </div>
        </div>
      </div>

      {error ? <div className="text-danger small mb-3">{error}</div> : null}

      <div style={LABELS_PANEL_STYLE}>
        <div className="card bg-dark text-white border-secondary mb-4">
          <div className="card-body">
            <div className="fw-semibold mb-3">New label</div>
            <div className="row g-3 align-items-end">
              <div className="col-md-6">
                <label htmlFor={CREATE_NAME_ID} className="form-label text-white-50 small">
                  Name
                </label>
                <input
                  id={CREATE_NAME_ID}
                  className={`form-control ${styles.lightInput}`}
                  value={newName}
                  onChange={(event) => setNewName(event.target.value)}
                  disabled={savingId === "new"}
                />
              </div>
              <div className="col-md-6">
                <button
                  className={`btn ${styles.darkActionButton}`}
                  onClick={handleCreate}
                  disabled={savingId === "new" || newName.trim().length === 0}
                >
                  Create label
                </button>
              </div>
            </div>
          </div>
        </div>

        {loading ? <div className="text-white-50 mb-3">Loading labels...</div> : null}

        {!loading && (
          <div className="w-100">
            <table className="table text-white table-striped table-hover mb-0 w-100">
              <thead>
                <tr>
                  <th className="align-middle">Name</th>
                  <th className="align-middle text-nowrap text-end">Galleries</th>
                  <th className="align-middle text-nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {labels.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-white-50 align-middle">
                      No labels yet
                    </td>
                  </tr>
                ) : (
                  labels.map((label) => {
                    const draft = nameDrafts[label._id] ?? label.name;
                    const hasChanges = draft.trim() !== label.name;
                    return (
                      <tr key={label._id}>
                        <td className="align-middle">
                          <input
                            aria-label={`Label name ${label.name}`}
                            className={`form-control form-control-sm ${styles.lightInput}`}
                            style={{ width: "100%", minWidth: "10rem" }}
                            value={draft}
                            onChange={(event) =>
                              setNameDrafts((current) => ({
                                ...current,
                                [label._id]: event.target.value,
                              }))
                            }
                            disabled={savingId === label._id}
                          />
                        </td>
                        <td
                          className="align-middle text-end"
                          style={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {label.galleryCount ?? "—"}
                        </td>
                        <td className="align-middle">
                          <div className="d-flex flex-wrap gap-2 align-items-center justify-content-start">
                            <button
                              type="button"
                              className={`btn btn-sm ${styles.darkActionButton}`}
                              onClick={() => handleSave(label)}
                              disabled={savingId === label._id || !hasChanges || draft.trim().length === 0}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => setLabelPendingDelete(label)}
                              disabled={savingId === label._id}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmModal
        show={labelPendingDelete !== null}
        onHide={() => setLabelPendingDelete(null)}
        onConfirm={async () => {
          if (!labelPendingDelete) return;
          const ok = await executeDeleteLabel(labelPendingDelete);
          if (ok) setLabelPendingDelete(null);
        }}
        title="Delete gallery label"
        message={
          labelPendingDelete ? (
            <>
              Delete label <strong>{labelPendingDelete.name}</strong>? This cannot be undone.
            </>
          ) : (
            ""
          )
        }
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};
